import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Mail, Lock, User, Phone, Building2, ArrowRight, Eye, EyeOff, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import SEO from '@/components/SEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Capitalize first letter of each word with Turkish character support
const capitalizeWords = (str) => {
  if (!str) return str;
  return str
    .split(' ')
    .map(word => {
      if (!word) return word;
      return word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR');
    })
    .join(' ');
};

// Password requirement item component
const PasswordRequirement = ({ met, text }) => (
  <div className={`flex items-center gap-2 text-xs transition-colors ${met ? 'text-emerald-500' : 'text-muted-foreground'}`}>
    {met ? (
      <CheckCircle className="h-3.5 w-3.5" />
    ) : (
      <div className="h-3.5 w-3.5 rounded-full border border-current" />
    )}
    <span>{text}</span>
  </div>
);

const TURNSTILE_SITEKEY = '0x4AAAAAACLpdpidtZFYh3Ns';

const RegisterPage = () => {
  const { language } = useLanguage();
  const { register, verifyOtp, googleLogin, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: form, 2: OTP verification
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState(''); // Store email for OTP verification
  const [otpCode, setOtpCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    company_name: '',
    heard_from: '',
    password: '',
    confirmPassword: '',
  });

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const translations = {
    tr: {
      title: 'Kayıt Ol',
      subtitle: 'Threat.live\'e katılın ve ağınızı koruyun',
      firstName: 'Ad',
      lastName: 'Soyad',
      phone: 'Telefon',
      phonePlaceholder: '+90 555 123 4567',
      email: 'E-posta',
      emailPlaceholder: 'ornek@sirket.com',
      companyName: 'Şirket Adı',
      companyPlaceholder: 'Opsiyonel',
      heardFrom: 'Bizi nereden duydunuz?',
      heardFromPlaceholder: 'Seçiniz',
      heardFromOptions: {
        google: 'Google',
        linkedin: 'LinkedIn',
        abuseipdb: 'AbuseIPDB',
        trnog: 'TRNOG',
        r10: 'R10.net',
        referral: 'Tavsiye',
        other: 'Diğer',
      },
      password: 'Şifre',
      passwordPlaceholder: 'Min. 8 karakter',
      confirmPassword: 'Şifre Tekrar',
      register: 'Kayıt Ol',
      registering: 'Kaydediliyor...',
      hasAccount: 'Zaten hesabınız var mı?',
      login: 'Giriş Yap',
      backToHome: 'Ana Sayfaya Dön',
      passwordMismatch: 'Şifreler eşleşmiyor',
      passwordWeak: 'Şifre en az 8 karakter, büyük/küçük harf, rakam ve özel karakter içermeli',
      turnstileError: 'Lütfen robot olmadığınızı doğrulayın',
      or: 'veya',
      googleRegister: 'Google ile Kayıt Ol',
      googleLoading: 'Google\'a yönlendiriliyor...',
      // Password requirements
      reqMinLength: 'En az 8 karakter',
      reqUppercase: 'En az bir büyük harf (A-Z)',
      reqLowercase: 'En az bir küçük harf (a-z)',
      reqNumber: 'En az bir rakam (0-9)',
      reqSpecial: 'En az bir özel karakter (@$!%*?()&-_.)',
      // OTP Step
      otpTitle: 'E-posta Doğrulama',
      otpSubtitle: 'E-posta adresinize gönderilen 6 haneli kodu girin',
      otpPlaceholder: '123456',
      verify: 'Doğrula',
      verifying: 'Doğrulanıyor...',
      resendOtp: 'Kodu tekrar gönder',
      otpSent: 'Doğrulama kodu e-posta adresinize gönderildi',
    },
    en: {
      title: 'Register',
      subtitle: 'Join Threat.live and protect your network',
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone',
      phonePlaceholder: '+1 555 123 4567',
      email: 'Email',
      emailPlaceholder: 'example@company.com',
      companyName: 'Company Name',
      companyPlaceholder: 'Optional',
      heardFrom: 'How did you hear about us?',
      heardFromPlaceholder: 'Select',
      heardFromOptions: {
        google: 'Google',
        linkedin: 'LinkedIn',
        abuseipdb: 'AbuseIPDB',
        trnog: 'TRNOG',
        r10: 'R10.net',
        referral: 'Referral',
        other: 'Other',
      },
      password: 'Password',
      passwordPlaceholder: 'Min. 8 characters',
      confirmPassword: 'Confirm Password',
      register: 'Register',
      registering: 'Registering...',
      hasAccount: 'Already have an account?',
      login: 'Sign In',
      backToHome: 'Back to Home',
      passwordMismatch: 'Passwords do not match',
      passwordWeak: 'Password must be at least 8 characters with uppercase, lowercase, number and special character',
      turnstileError: 'Please verify you are not a robot',
      or: 'or',
      googleRegister: 'Sign up with Google',
      googleLoading: 'Redirecting to Google...',
      // Password requirements
      reqMinLength: 'At least 8 characters',
      reqUppercase: 'At least one uppercase letter (A-Z)',
      reqLowercase: 'At least one lowercase letter (a-z)',
      reqNumber: 'At least one number (0-9)',
      reqSpecial: 'At least one special character (@$!%*?()&-_.)',
      // OTP Step
      otpTitle: 'Email Verification',
      otpSubtitle: 'Enter the 6-digit code sent to your email',
      otpPlaceholder: '123456',
      verify: 'Verify',
      verifying: 'Verifying...',
      resendOtp: 'Resend code',
      otpSent: 'Verification code sent to your email',
    }
  };

  const txt = translations[language] || translations.en;

  // Initialize Turnstile
  useEffect(() => {
    if (step === 1) {
      const initTurnstile = () => {
        if (window.turnstile && turnstileRef.current && !turnstileRef.current.hasChildNodes()) {
          window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITEKEY,
            callback: (token) => setTurnstileToken(token),
            'expired-callback': () => setTurnstileToken(null),
            'error-callback': () => setTurnstileToken(null),
            theme: 'auto',
          });
        }
      };

      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          initTurnstile();
        }
      }, 100);

      setTimeout(() => clearInterval(checkTurnstile), 10000);
      return () => clearInterval(checkTurnstile);
    }
  }, [step]);

  const validatePassword = (password) => {
    if (password.length < 8) return false;
    if (!/[a-z]/.test(password)) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/\d/.test(password)) return false;
    if (!/[^A-Za-z0-9]/.test(password)) return false;
    return true;
  };

  // Password requirement checks
  const passwordChecks = {
    minLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /\d/.test(formData.password),
    hasSpecial: /[@$!%*?()&\-_.]/.test(formData.password),
  };

  const allPasswordRequirementsMet = Object.values(passwordChecks).every(Boolean);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-capitalize each word for name fields (Turkish character support)
    if (name === 'first_name' || name === 'last_name') {
      setFormData({ ...formData, [name]: capitalizeWords(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setError('');
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    setError('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError(txt.passwordMismatch);
      return;
    }

    if (!allPasswordRequirementsMet) {
      setError(txt.passwordWeak);
      return;
    }

    if (!turnstileToken) {
      setError(txt.turnstileError);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const data = await register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        email: formData.email,
        company_name: formData.company_name || undefined,
        heard_from: formData.heard_from || undefined,
        password: formData.password,
      });

      if (data.status === 'success') {
        setRegisteredEmail(formData.email); // Store email for OTP verification
        setStep(2);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setIsGoogleLoading(true);
    setError('');
    try {
      await googleLogin();
    } catch (err) {
      setError(err.message);
      setIsGoogleLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const data = await verifyOtp(registeredEmail, otpCode); // Use email instead of userId

      if (data.status === 'success') {
        navigate('/dashboard');
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 py-12 relative overflow-hidden" data-testid="register-page">
      <SEO page="register" />
      
      {/* Background Effects */}
      <div className="absolute inset-0 grid-bg mask-radial-fade opacity-50 pointer-events-none"></div>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="relative sharp p-3 bg-primary">
              <Shield className="h-7 w-7 text-primary-foreground" />
            </div>
            <span className="text-2xl font-bold tracking-tight">
              Threat<span className="text-primary">.</span>live
            </span>
          </Link>
        </div>

        <Card className="sharp border-border bg-card/50 backdrop-blur shadow-xl">
          {step === 1 ? (
            /* Registration Form */
            <>
              <CardHeader className="text-center pb-2 border-b border-border bg-muted/30">
                <div className="soc-badge mx-auto mb-3">
                  <span className="status-dot" />
                  <span>// REGISTRATION · SECURE</span>
                </div>
                <CardTitle className="text-xl font-mono uppercase tracking-wider">{txt.title}</CardTitle>
                <CardDescription className="text-xs">{txt.subtitle}</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleRegister} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="bg-red-500/10 border-red-500/30">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{txt.firstName}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{txt.lastName}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{txt.phone}</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        name="phone"
                        type="tel"
                        placeholder={txt.phonePlaceholder}
                        value={formData.phone}
                        onChange={handleChange}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{txt.email}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        name="email"
                        type="email"
                        placeholder={txt.emailPlaceholder}
                        value={formData.email}
                        onChange={handleChange}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{txt.companyName}</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        name="company_name"
                        placeholder={txt.companyPlaceholder}
                        value={formData.company_name}
                        onChange={handleChange}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{txt.heardFrom}</Label>
                    <Select value={formData.heard_from} onValueChange={(value) => handleSelectChange('heard_from', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder={txt.heardFromPlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Google">{txt.heardFromOptions.google}</SelectItem>
                        <SelectItem value="LinkedIn">{txt.heardFromOptions.linkedin}</SelectItem>
                        <SelectItem value="AbuseIPDB">{txt.heardFromOptions.abuseipdb}</SelectItem>
                        <SelectItem value="TRNOG">{txt.heardFromOptions.trnog}</SelectItem>
                        <SelectItem value="R10.net">{txt.heardFromOptions.r10}</SelectItem>
                        <SelectItem value="Tavsiye">{txt.heardFromOptions.referral}</SelectItem>
                        <SelectItem value="Diğer">{txt.heardFromOptions.other}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{txt.password}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={txt.passwordPlaceholder}
                          value={formData.password}
                          onChange={handleChange}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{txt.confirmPassword}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          name="confirmPassword"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className="pl-10 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Password Requirements */}
                  {formData.password && (
                    <div className="p-3 rounded-lg bg-muted/50 border space-y-1.5">
                      <PasswordRequirement met={passwordChecks.minLength} text={txt.reqMinLength} />
                      <PasswordRequirement met={passwordChecks.hasUppercase} text={txt.reqUppercase} />
                      <PasswordRequirement met={passwordChecks.hasLowercase} text={txt.reqLowercase} />
                      <PasswordRequirement met={passwordChecks.hasNumber} text={txt.reqNumber} />
                      <PasswordRequirement met={passwordChecks.hasSpecial} text={txt.reqSpecial} />
                    </div>
                  )}

                  {/* Turnstile */}
                  <div ref={turnstileRef} className="flex justify-center" />

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700"
                    disabled={isLoading || !turnstileToken}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {txt.registering}
                      </>
                    ) : (
                      <>
                        {txt.register}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border"></div>
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">{txt.or}</span>
                    </div>
                  </div>

                  {/* Google Register Button */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-4 gap-2"
                    onClick={handleGoogleRegister}
                    disabled={isGoogleLoading}
                    data-testid="google-register-btn"
                  >
                    {isGoogleLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {txt.googleLoading}
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="currentColor"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        {txt.googleRegister}
                      </>
                    )}
                  </Button>
                </div>

                <p className="mt-6 text-center text-sm text-muted-foreground">
                  {txt.hasAccount}{' '}
                  <Link to="/login" className="text-emerald-500 hover:underline font-medium">
                    {txt.login}
                  </Link>
                </p>
              </CardContent>
            </>
          ) : (
            /* OTP Verification */
            <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl flex items-center justify-center gap-2">
                  <Mail className="h-6 w-6 text-emerald-500" />
                  {txt.otpTitle}
                </CardTitle>
                <CardDescription>{txt.otpSubtitle}</CardDescription>
              </CardHeader>
              <CardContent>
                <Alert className="mb-4 bg-emerald-500/10 border-emerald-500/30">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <AlertDescription className="text-emerald-600 dark:text-emerald-400">
                    {txt.otpSent}
                  </AlertDescription>
                </Alert>

                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="bg-red-500/10 border-red-500/30">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label>OTP</Label>
                    <Input
                      type="text"
                      placeholder={txt.otpPlaceholder}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center text-2xl tracking-widest"
                      maxLength={6}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600"
                    disabled={isLoading || otpCode.length !== 6}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {txt.verifying}
                      </>
                    ) : (
                      <>
                        {txt.verify}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        <p className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            ← {txt.backToHome}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
